// Which scan a page is showing, and where its sibling pages live.
//
// Pure, so the one navigation rule that matters can be tested: a link pressed
// while reading an old scan must stay in that scan. The bare paths no longer
// resolve to "the newest scan" — they mean the whole history, and Market.tsx
// and its siblings serve them without a uid at all.
import type { ScanSummary } from "../api/types";

/** The four pages, in nav order. `packages` is plural in the URL and singular
 *  in `scans.modes`, because the route names a page and the column names a
 *  mode; `scanWalked` is the one place that difference is spelled out. */
export const MODE_PAGES = ["buy", "sell", "craft", "packages"] as const;
export type ModePage = (typeof MODE_PAGES)[number];

/** An empty uid means "not inside a scan", so the link goes to the all-time
 *  page — every item ever scanned, not the newest walk. Shell.tsx passes `""`
 *  on every bare page, which is what keeps the nav there pointing at itself. */
export function modeHref(uid: string, mode: ModePage): string {
  return uid ? `/scans/${encodeURIComponent(uid)}/${mode}` : `/${mode}`;
}

/** The one place "packages" (the route, the nav, /scans) and "package" (what
 *  scans.modes stores) diverge, so scanWalked and displayModes share a single
 *  translation instead of drifting into two — which is how the index ended up
 *  printing the raw column value ("package") in its meta line. */
const STORED_MODE: Record<ModePage, string> = {
  buy: "buy",
  sell: "sell",
  craft: "craft",
  packages: "package",
};

/** Did this scan walk that mode at all? An unknown scan answers true: the scans
 *  list holds only the 50 most recent, and telling someone an older scan "didn't
 *  walk craft" on no evidence is worse than showing them an ordinary empty. */
export function scanWalked(scan: ScanSummary | undefined, mode: ModePage): boolean {
  if (!scan) return true;
  return scan.modes.includes(STORED_MODE[mode]);
}

/** scan.modes as the rest of the UI spells them — reverses STORED_MODE so a
 *  scan's meta line agrees with the nav and /scans about "packages" instead of
 *  printing the column's own singular. Anything not in STORED_MODE (there is
 *  nothing today) passes through unchanged rather than vanishing. */
export function displayModes(modes: string[]): string[] {
  const display = new Map(Object.entries(STORED_MODE).map(([page, stored]) => [stored, page]));
  return modes.map((mode) => display.get(mode) ?? mode);
}
